// palette.ts — the colour half of the brand contract, as PURE DATA.
//
// This module is deliberately free of React, JSX and asset imports so that it
// can be imported from two very different places:
//   1. the app (src/brand/themes/*.ts composes a palette with its assets), and
//   2. vite.config.ts, which inlines the ACTIVE brand's tokens into index.html
//      at build time so the very first paint is already the right colour.
// Adding an import of anything renderable here breaks (2) — keep it plain.
//
// Values are hex because that is what a designer hands over. Tailwind consumes
// them as space-separated RGB triplets (`rgb(var(--x) / <alpha-value>)`, see
// tailwind.config.ts), so `brandTokensCss` does the hex → "R G B" conversion in
// one place rather than asking every brand to spell its colours twice.
//
// The token NAMES mirror src/styles/tokens.css one-for-one: this layer swaps
// the values a brand paints with, never the set of things that can be painted.

/** Every colour token a brand must supply. Keys map to the CSS var of the same
 *  name in kebab-case (`bgSubtle` → `--bg-subtle`). */
export interface BrandColorTokens {
  // canvas & surfaces
  bg: string;
  bgSubtle: string;
  surface: string;
  surface2: string;
  surface3: string;
  input: string;
  overlay: string;

  // text
  fg: string;
  fgMuted: string;
  fgSubtle: string;

  // lines
  border: string;
  borderStrong: string;
  divider: string;
  ring: string;

  // intents
  primary: string;
  primaryHover: string;
  primaryFg: string;
  primarySubtle: string;
  secondary: string;
  secondaryHover: string;
  secondaryFg: string;
  success: string;
  successFg: string;
  danger: string;
  dangerFg: string;
  warning: string;
  warningFg: string;

  /** Tint of the currency glyph where it is drawn from `currentColor`. */
  spark: string;

  // chrome flourishes the semantic tokens above can't express on their own —
  // the rail gradient's two stops, the ambient orbs, and the two flat panels
  // inside the sidebar. Previously hard-coded hex in app.css / Sidebar.tsx.
  railTop: string;
  railBottom: string;
  glow: string;
  surfaceRail: string;
  surfaceRail2: string;
  /** Far stop of the fallback-avatar gradient (near stop is `secondary`). */
  avatarGradientTo: string;

  // Rank podium (SM-334 follow-up). The three blocks under the top-3 players
  // used to be flat SVG files in public/rank/ with brand hex baked in, which
  // no theme could reach; they are now drawn inline from these tokens. Five
  // fills, matching the original artwork: the winner's block carries a
  // two-stop gradient plus a lighter top edge, the runners-up a flat face plus
  // their own edge.
  podiumWinnerTop: string;
  podiumWinnerBottom: string;
  podiumWinnerEdge: string;
  podiumFace: string;
  podiumEdge: string;
  /** Rank numeral painted on the block — needs contrast against the faces. */
  podiumNumeral: string;
}

export const BRAND_TOKEN_KEYS = [
  "bg",
  "bgSubtle",
  "surface",
  "surface2",
  "surface3",
  "input",
  "overlay",
  "fg",
  "fgMuted",
  "fgSubtle",
  "border",
  "borderStrong",
  "divider",
  "ring",
  "primary",
  "primaryHover",
  "primaryFg",
  "primarySubtle",
  "secondary",
  "secondaryHover",
  "secondaryFg",
  "success",
  "successFg",
  "danger",
  "dangerFg",
  "warning",
  "warningFg",
  "spark",
  "railTop",
  "railBottom",
  "glow",
  "surfaceRail",
  "surfaceRail2",
  "avatarGradientTo",
  "podiumWinnerTop",
  "podiumWinnerBottom",
  "podiumWinnerEdge",
  "podiumFace",
  "podiumEdge",
  "podiumNumeral",
] as const satisfies readonly (keyof BrandColorTokens)[];

// ── SmartMarket ────────────────────────────────────────────────────────────
// The app's original look, unchanged: the deep-purple "game" theme extracted
// from SmartMarket.fig / the CrazyBet reference. These values are mirrored by
// the `[data-theme="game"]` block in src/styles/tokens.css, which is the
// pre-JavaScript fallback — src/brand/__tests__/brandTheme.test.ts asserts the
// two agree, so the mirror can't silently drift.
export const SMARTMARKET_COLORS: BrandColorTokens = {
  bg: "#06071e",
  bgSubtle: "#1e123a",
  surface: "#261746",
  surface2: "#34205c",
  surface3: "#462c78",
  input: "#1e123a",
  overlay: "#080414",

  fg: "#f5f0ff",
  fgMuted: "#b2a2d2",
  fgSubtle: "#8070a2",

  border: "#3a2860",
  borderStrong: "#604292",
  divider: "#322252",
  ring: "#a855f7",

  primary: "#9333ea",
  primaryHover: "#a855f7",
  primaryFg: "#ffffff",
  primarySubtle: "#40286e",
  secondary: "#f09d34",
  secondaryHover: "#f5b85c",
  secondaryFg: "#2e1e04",
  success: "#4ade80",
  successFg: "#081e10",
  danger: "#f43f5e",
  dangerFg: "#ffffff",
  warning: "#facc15",
  warningFg: "#291e00",

  spark: "#ffd75c",

  railTop: "#121428",
  railBottom: "#080a1b",
  glow: "#aa82ff",
  surfaceRail: "#1b1939",
  surfaceRail2: "#211e3a",
  avatarGradientTo: "#4c1d95",

  // Lifted verbatim from the retired public/rank/pedestal-*.svg files, so the
  // podium paints exactly as it always did.
  podiumWinnerTop: "#7c5cff",
  podiumWinnerBottom: "#b6a4ff",
  podiumWinnerEdge: "#d8ceff",
  podiumFace: "#8e78e0",
  podiumEdge: "#bba9f5",
  podiumNumeral: "#ffffff",
};

// ── RichPoll ───────────────────────────────────────────────────────────────
// The gold/amber white-label. The three brand hues are sampled straight from
// the supplied artwork (Richpoll-Logo-01/02.png): #ffb703 amber, #f99300 deep
// orange, #ffcc29 light gold. The canvas and surface ramp keep SmartMarket's
// lightness steps — same depth, same contrast behaviour — rotated from violet
// to a warm brown so the gold marks sit on their own family of colour.
//
// YES/NO (success/danger) stay the SmartMarket green and rose on purpose:
// they carry TRADING meaning, not brand identity, and re-hueing them would
// change how a position reads. Only identity colours differ between brands.
export const RICHPOLL_COLORS: BrandColorTokens = {
  bg: "#130d03",
  bgSubtle: "#2a1c05",
  surface: "#33220a",
  surface2: "#453009",
  surface3: "#5c400c",
  input: "#2a1c05",
  overlay: "#140d02",

  fg: "#fff8ea",
  fgMuted: "#d6bd8c",
  fgSubtle: "#a08a5e",

  border: "#56400f",
  borderStrong: "#8a6716",
  divider: "#45330c",
  ring: "#ffcc29",

  primary: "#ffb703",
  primaryHover: "#ffcc29",
  // Amber is a LIGHT colour: its foreground has to be dark to stay readable,
  // where SmartMarket's purple takes white. Same token, opposite end.
  primaryFg: "#2b1a00",
  primarySubtle: "#4a3406",
  secondary: "#f99300",
  secondaryHover: "#ffb703",
  secondaryFg: "#2e1a04",
  success: "#4ade80",
  successFg: "#081e10",
  danger: "#f43f5e",
  dangerFg: "#ffffff",
  warning: "#facc15",
  warningFg: "#291e00",

  spark: "#ffcc29",

  railTop: "#241703",
  railBottom: "#0c0801",
  glow: "#ffb703",
  surfaceRail: "#221803",
  surfaceRail2: "#2b1f05",
  avatarGradientTo: "#7c4a03",

  // The same light-to-dark ladder as SmartMarket's podium, in the brand's
  // amber family, so the blocks still read as three-dimensional. The numeral
  // flips to dark brown: white on gold is illegible, which is the same reason
  // primaryFg is dark for this brand.
  podiumWinnerTop: "#f99300",
  podiumWinnerBottom: "#ffc93c",
  podiumWinnerEdge: "#ffe08a",
  podiumFace: "#e0a21a",
  podiumEdge: "#ffcc66",
  podiumNumeral: "#3b2400",
};

export const BRAND_PALETTES = {
  smartmarket: SMARTMARKET_COLORS,
  richpoll: RICHPOLL_COLORS,
} as const;

/** `#06071e` → `6 7 30`. Accepts 3- and 6-digit hex. */
export function hexToRgbTriplet(hex: string): string {
  const h = hex.trim().replace(/^#/, "");
  const full = h.length === 3 ? h[0] + h[0] + h[1] + h[1] + h[2] + h[2] : h;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) throw new Error(`brand: not a hex colour: ${hex}`);
  const n = parseInt(full, 16);
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
}

const CSS_VAR = (key: keyof BrandColorTokens) => `--${key.replace(/([A-Z0-9])/g, "-$1").toLowerCase()}`;

/**
 * The full set of CSS custom properties a brand contributes, as
 * `{ "--bg": "6 7 30", … }`. Two shapes come out of one palette:
 *   - the Tailwind-facing triplets (every token above), and
 *   - the handful of derived values that consumers read as whole colours:
 *     the hex aliases `lib/outcomes.ts` reads inline, the soft YES/NO washes,
 *     and the rail gradients composed in app.css.
 */
export function brandCssVars(colors: BrandColorTokens): Record<string, string> {
  const rgb = (k: keyof BrandColorTokens) => hexToRgbTriplet(colors[k]);
  const vars: Record<string, string> = {};
  for (const key of BRAND_TOKEN_KEYS) vars[CSS_VAR(key)] = rgb(key);

  // Chart lines follow the trading intents (as the game theme already did).
  vars["--chart-up"] = "var(--success)";
  vars["--chart-dn"] = "var(--danger)";

  // Whole-colour aliases for the inline-style consumers in lib/outcomes.ts.
  vars["--yes"] = colors.success;
  vars["--no"] = colors.danger;
  vars["--warn"] = colors.warning;
  vars["--t-3"] = colors.fgMuted;
  vars["--yes-soft"] = `rgb(${rgb("success")} / 0.16)`;
  vars["--no-soft"] = `rgb(${rgb("danger")} / 0.16)`;

  // Rail gradients — the sidebar settles to its own darker bottom, the content
  // card fades into the page canvas. Composed here so both stay in sync with
  // the palette instead of carrying hex in app.css.
  vars["--rail-gradient"] =
    `linear-gradient(180deg, ${colors.railTop} 0%, rgb(var(--bg)) 52%, rgb(var(--bg)) 100%)`;
  vars["--rail-gradient-sidebar"] =
    `linear-gradient(180deg, ${colors.railTop} 0%, ${colors.railBottom} 52%, ${colors.railBottom} 100%)`;

  return vars;
}

/**
 * The same variables as a stylesheet rule, for build-time inlining. Scoped to
 * `:root[data-brand="<id>"]` (specificity 0,2,0) so it outranks the
 * `[data-theme="game"]` fallback block in tokens.css without `!important`.
 */
export function brandTokensCss(brandId: string, colors: BrandColorTokens): string {
  const body = Object.entries(brandCssVars(colors))
    .map(([k, v]) => `  ${k}: ${v};`)
    .join("\n");
  return `:root[data-brand="${brandId}"] {\n${body}\n}`;
}
